import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest, apiUrl } from "../../helpers/api";
import { getToken } from "../../helpers/authHelper";
import { useAuthenticatedMedia } from "../AuthenticatedMedia";
import { Can } from "../AccessContext";
import "./ProductDetailsEditor.css";

// Product and service details, shared by onboarding and the Business Profile page so both save the same data
// under the same rules. Each item is a product or a service, and only shows the fields for its type.
// Product images live in the business media library; AI image and video generation uses them as references
// for the product's real appearance (the first image is the main one).

export const MAX_PRODUCTS = 7;
export const MAX_PRODUCT_IMAGES = 4;
const MAX_LIST_ENTRIES = 10;
const maxImageBytes = 10 * 1024 * 1024;
const imageTypes = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const libraryImagePattern = /^\/api\/media\/(\d+)\/file$/;
const storedImagePattern = /^\/(?:api\/media\/\d+\/file|downloads\/[A-Za-z0-9._-]+)$/;
const editPermission = "user.business_profile.edit";
const limits = { name: 120, description: 1200, category: 80, pricing: 120, duration: 80 };
export const limitMessage = `You can add up to ${MAX_PRODUCTS} products or services.`;

let nextKey = 0;
const productKey = () => `product-${Date.now().toString(36)}-${(nextKey += 1)}`;

// A new item has no type until the customer chooses one.
export const emptyProduct = () => ({
  _key: productKey(), type: "", name: "", description: "", category: "",
  price: "", images: [], features: [], pricing: "", duration: "", benefits: []
});

const textOf = (value) => typeof value === "string" ? value : "";
const listOf = (value) => Array.isArray(value) ? value.filter((entry) => typeof entry === "string") : [];

export const normalizeProducts = (value) => {
  let list = value;
  if (typeof list === "string") {
    try { list = JSON.parse(list); } catch { list = []; }
  }
  return (Array.isArray(list) ? list : [])
    .filter((product) => product && typeof product === "object" && !Array.isArray(product))
    .map((product) => ({
      _key: product._key || productKey(),
      // Items saved before the type existed were all products.
      type: ["product", "service"].includes(product.type) ? product.type : product._key ? "" : "product",
      name: textOf(product.name),
      description: textOf(product.description),
      category: textOf(product.category),
      price: product.price ?? "",
      images: Array.isArray(product.images) ? listOf(product.images) : product.imageUrl ? [product.imageUrl] : [],
      features: listOf(product.features),
      pricing: textOf(product.pricing),
      duration: textOf(product.duration),
      benefits: listOf(product.benefits)
    }));
};

// What the API stores: only the fields for each item's type, without the client-only key.
export const productsForSave = (products) => products.map((product) => {
  const common = { type: product.type, name: product.name, description: product.description, category: product.category };
  return product.type === "service"
    ? { ...common, pricing: product.pricing, duration: product.duration, benefits: product.benefits }
    : { ...common, price: product.price, images: product.images, features: product.features };
});

// Mirrors the API's rules. A list saved before the limit existed may be kept but not grown.
export const validateProducts = (products, { previousCount = 0 } = {}) => {
  if (products.length > MAX_PRODUCTS && products.length > previousCount) return limitMessage;
  for (let index = 0; index < products.length; index += 1) {
    const product = products[index];
    if (!["product", "service"].includes(product.type)) return `Choose Product or Service for item ${index + 1}.`;
    const kind = product.type === "service" ? "Service" : "Product";
    if (!product.name.trim()) return `${kind} ${index + 1} needs a name.`;
    if (Object.entries(limits).some(([field, limit]) => (product[field] || "").trim().length > limit)) return `${kind} ${index + 1} exceeds a field length limit.`;
    const list = product.type === "service" ? product.benefits : product.features;
    if (list.length > MAX_LIST_ENTRIES) return `Add no more than ${MAX_LIST_ENTRIES} ${product.type === "service" ? "benefits" : "features"} for item ${index + 1}.`;
    if (product.type === "service") continue;
    if (product.price !== "" && product.price != null && (!Number.isFinite(Number(product.price)) || Number(product.price) < 0 || Number(product.price) > 999999999)) {
      return `Enter a valid non-negative price for product ${index + 1}.`;
    }
    if (product.images.length > MAX_PRODUCT_IMAGES) return `Add no more than ${MAX_PRODUCT_IMAGES} images for product ${index + 1}.`;
    if (product.images.some((url) => !storedImagePattern.test(url))) return `Product ${index + 1} images must be stored media-library images.`;
  }
  return "";
};

const checkImageFile = (file) => {
  if (!imageTypes.includes(file.type)) return `${file.name}: choose a PNG, JPEG, WebP, or GIF image.`;
  if (!file.size || file.size > maxImageBytes) return `${file.name}: choose a non-empty image no larger than 10 MB.`;
  return "";
};

const uploadProductImage = async (file) => {
  const response = await fetch(apiUrl("/api/brand-settings/product-media"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getToken() || ""}`,
      "Content-Type": "application/octet-stream",
      "X-File-Name": encodeURIComponent(file.name),
      "X-File-Type": file.type
    },
    body: file
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.message || `Unable to upload ${file.name}.`);
  if (!result.media?.url) throw new Error(`The upload for ${file.name} did not return an image.`);
  return result.media;
};

// Library images are read through the business-profile endpoints, which also work during onboarding.
const previewSource = (imageUrl, size) => {
  const id = imageUrl?.match(libraryImagePattern)?.[1];
  return id ? `/api/brand-settings/product-media/${id}/${size}` : imageUrl || "";
};

const ProductImage = ({ imageUrl, large = false, alt = "" }) => {
  const [useFile, setUseFile] = useState(large);
  const { url, error } = useAuthenticatedMedia(previewSource(imageUrl, useFile ? "file" : "thumbnail"));
  useEffect(() => { setUseFile(large); }, [imageUrl, large]);
  useEffect(() => { if (error && !useFile) setUseFile(true); }, [error, useFile]);
  if (error && useFile) return <span className="pd-image-missing"><i className="ti ti-photo-off" aria-hidden="true" />Image unavailable</span>;
  if (!url) return <span className="pd-image-loading" aria-label="Loading image" />;
  return <img src={url} alt={alt} />;
};

// Escape closes the dialog; focus returns to whatever opened it.
const useDialog = (onClose) => {
  const ref = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement;
    const onKey = (event) => { if (event.key === "Escape") close.current(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, []);
  return ref;
};

const ImageLightbox = ({ imageUrl, name, onClose }) => {
  const ref = useDialog(onClose);
  return <div className="pd-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section ref={ref} className="pd-lightbox" role="dialog" aria-modal="true" aria-label={`Preview of ${name || "product image"}`}>
      <button type="button" className="pd-icon-button pd-lightbox-close" onClick={onClose} aria-label="Close image preview" autoFocus><i className="ti ti-x" aria-hidden="true" /></button>
      <ProductImage imageUrl={imageUrl} large alt={`${name || "Product"} reference`} />
    </section>
  </div>;
};

// Single-choice picker over the business media library's images, with upload from the device.
export const ProductImagePicker = ({ currentUrl, onClose, onSelect }) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const uploadRef = useRef(null);
  const dialogRef = useDialog(onClose);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError("");
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ page: String(page), limit: "24", sort: "newest" });
      if (search.trim()) params.set("search", search.trim());
      apiRequest(`/api/brand-settings/product-media?${params}`).then((result) => {
        if (!current) return;
        setItems((existing) => page === 1 ? result.media || [] : [...existing, ...(result.media || [])]);
        setHasMore(Boolean(result.hasMore));
      }).catch((requestError) => {
        if (current) setError(requestError.message || "Unable to load your media library.");
      }).finally(() => { if (current) setLoading(false); });
    }, search ? 250 : 0);
    return () => { current = false; clearTimeout(timer); };
  }, [page, search]);

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const fileError = checkImageFile(file);
    if (fileError) { setError(fileError); return; }
    setUploading(true);
    setError("");
    try {
      const media = await uploadProductImage(file);
      setItems((existing) => [media, ...existing.filter((item) => item.id !== media.id)]);
      setSelected(media);
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setUploading(false);
    }
  };

  return <div className="pd-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section ref={dialogRef} className="pd-picker" role="dialog" aria-modal="true" aria-labelledby="pd-picker-title">
      <header className="pd-picker-header">
        <div><p className="pd-eyebrow">MEDIA GALLERY</p><h2 id="pd-picker-title">Choose a product image</h2></div>
        <div className="pd-picker-header-actions">
          <Can permission={editPermission}><button type="button" className="pd-button is-secondary" onClick={() => uploadRef.current?.click()} disabled={uploading}>
            <i className={`ti ${uploading ? "ti-loader-2 pd-spin" : "ti-upload"}`} aria-hidden="true" />{uploading ? "Uploading..." : "Upload new"}
          </button></Can>
          <input ref={uploadRef} className="pd-file-input" type="file" accept={imageTypes.join(",")} onChange={upload} tabIndex={-1} aria-label="Upload a product image to the media gallery" />
          <button type="button" className="pd-icon-button" onClick={onClose} aria-label="Close media gallery"><i className="ti ti-x" aria-hidden="true" /></button>
        </div>
      </header>
      <label className="pd-search"><i className="ti ti-search" aria-hidden="true" /><input autoFocus value={search} onChange={(event) => { setPage(1); setSearch(event.target.value); }} placeholder="Search images" aria-label="Search images" /></label>
      {error && <p className="pd-error" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" />{error}</p>}
      {loading && page === 1
        ? <div className="pd-picker-grid" aria-label="Loading images">{Array.from({ length: 8 }, (_, index) => <span className="pd-picker-skeleton" key={index} />)}</div>
        : items.length
          ? <div className="pd-picker-grid" role="listbox" aria-label="Images in your media gallery">{items.map((item) => {
            const isSelected = selected ? selected.id === item.id : item.url === currentUrl;
            return <button type="button" role="option" aria-selected={isSelected} key={item.id} className={`pd-picker-item${isSelected ? " is-selected" : ""}`} onClick={() => setSelected(item)} onDoubleClick={() => onSelect(item)} title={item.name}>
              <ProductImage imageUrl={item.url} />
              <span className="pd-picker-check" aria-hidden="true"><i className="ti ti-check" /></span>
              <span className="pd-picker-name">{item.name}</span>
            </button>;
          })}</div>
          : <div className="pd-picker-empty"><i className="ti ti-photo-search" aria-hidden="true" /><strong>{search ? "No matching images" : "No images yet"}</strong><span>Upload a product photo from your device to add it to your gallery.</span></div>}
      {hasMore && <button type="button" className="pd-button is-quiet pd-load-more" disabled={loading} onClick={() => setPage((value) => value + 1)}>{loading ? "Loading..." : "Load more"}</button>}
      <footer className="pd-picker-footer">
        <span>Images from your gallery stay there when removed from a product.</span>
        <div>
          <button type="button" className="pd-button is-quiet" onClick={onClose}>Cancel</button>
          <button type="button" className="pd-button is-primary" disabled={!selected} onClick={() => onSelect(selected)}>Use image</button>
        </div>
      </footer>
    </section>
  </div>;
};

// One image tile: a saved image with preview, replace and remove, or (without imageUrl) the tile that adds one.
const ProductImageSlot = ({ imageUrl, label, isMain, uploading, onUpload, onOpenGallery, onRemove }) => {
  const fileRef = useRef(null);
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const closePreview = useCallback(() => setPreviewing(false), []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOutside = (event) => { if (!menuRef.current?.contains(event.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", closeOutside);
    return () => document.removeEventListener("mousedown", closeOutside);
  }, [menuOpen]);

  const chooseFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    setMenuOpen(false);
    if (file) onUpload(file);
  };
  const drop = (event) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer?.files?.[0];
    if (file && !uploading) onUpload(file);
  };

  return <div className="pd-image">
    <div
      className={`pd-image-frame${imageUrl ? " has-image" : ""}${dragging ? " is-dragging" : ""}`}
      onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={drop}
    >
      {imageUrl
        ? <button type="button" className="pd-image-preview" onClick={() => setPreviewing(true)} aria-label={`Preview ${label}`}><ProductImage imageUrl={imageUrl} alt="" /><span className="pd-image-zoom" aria-hidden="true"><i className="ti ti-zoom-in" /></span></button>
        : <div className="pd-image-empty"><i className="ti ti-photo-plus" aria-hidden="true" /><span>Drop a photo here</span></div>}
      {isMain && <span className="pd-image-badge">Main</span>}
      {uploading && <div className="pd-image-busy" role="status"><span className="pd-spinner" aria-hidden="true" />Uploading...</div>}
    </div>
    <input ref={fileRef} className="pd-file-input" type="file" accept={imageTypes.join(",")} onChange={chooseFile} tabIndex={-1} aria-label={`Upload ${label} from your device`} />
    <Can permission={editPermission}>
      {imageUrl
        ? <div className="pd-image-actions">
          <div className="pd-menu-wrap" ref={menuRef}>
            <button type="button" className="pd-button is-secondary is-small" aria-haspopup="menu" aria-expanded={menuOpen} aria-label={`Replace ${label}`} disabled={uploading} onClick={() => setMenuOpen((open) => !open)}><i className="ti ti-replace" aria-hidden="true" />Replace</button>
            {menuOpen && <div className="pd-menu" role="menu" onKeyDown={(event) => { if (event.key === "Escape") setMenuOpen(false); }}>
              <button type="button" role="menuitem" autoFocus onClick={() => fileRef.current?.click()}><i className="ti ti-device-desktop-up" aria-hidden="true" />Upload from device</button>
              <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); onOpenGallery(); }}><i className="ti ti-photo-search" aria-hidden="true" />Choose from gallery</button>
            </div>}
          </div>
          <button type="button" className="pd-button is-danger is-small pd-image-remove" disabled={uploading} onClick={onRemove} aria-label={`Remove ${label}`}><i className="ti ti-trash" aria-hidden="true" /></button>
        </div>
        : <div className="pd-image-actions">
          <button type="button" className="pd-button is-secondary is-small" disabled={uploading} onClick={() => fileRef.current?.click()} aria-label={`Upload ${label}`}><i className="ti ti-upload" aria-hidden="true" />Upload</button>
          <button type="button" className="pd-button is-secondary is-small" disabled={uploading} onClick={onOpenGallery} aria-label={`Choose ${label} from gallery`}><i className="ti ti-photo-search" aria-hidden="true" />Gallery</button>
        </div>}
    </Can>
    {previewing && imageUrl && <ImageLightbox imageUrl={imageUrl} name={label} onClose={closePreview} />}
  </div>;
};

// Short text entries (product features, service benefits) added one at a time.
const ListInput = ({ id, label, entries, placeholder, inputClassName, onChange }) => {
  const [draft, setDraft] = useState("");
  const full = entries.length >= MAX_LIST_ENTRIES;
  const add = () => {
    const value = draft.trim();
    if (!value || full || entries.some((entry) => entry.toLowerCase() === value.toLowerCase())) return;
    onChange([...entries, value]);
    setDraft("");
  };
  return <div className="pd-field pd-span">
    <label className="pd-label" htmlFor={id}>{label}<span className="pd-optional">Optional · up to {MAX_LIST_ENTRIES}</span></label>
    {entries.length > 0 && <ul className="pd-chips" aria-label={label}>
      {entries.map((entry, index) => <li key={`${entry}-${index}`} className="pd-chip">
        <span>{entry}</span>
        <Can permission={editPermission}><button type="button" aria-label={`Remove ${entry}`} onClick={() => onChange(entries.filter((_, entryIndex) => entryIndex !== index))}><i className="ti ti-x" aria-hidden="true" /></button></Can>
      </li>)}
    </ul>}
    <div className="pd-list-entry">
      <input id={id} className={inputClassName} maxLength={120} value={draft} placeholder={full ? `Maximum of ${MAX_LIST_ENTRIES} reached` : placeholder} disabled={full}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); add(); } }} />
      <button type="button" className="pd-button is-secondary" disabled={full || !draft.trim()} onClick={add} aria-label={`Add to ${label.toLowerCase()}`}><i className="ti ti-plus" aria-hidden="true" />Add</button>
    </div>
  </div>;
};

const typeOptions = [
  ["product", "Product", "ti-package", "Something you sell, with photos"],
  ["service", "Service", "ti-briefcase", "Work you do for customers"]
];

const ProductDetailsEditor = ({ products, onChange, onUploadingChange, variant = "profile", inputClassName = "pd-input", title = "Product & service details", description }) => {
  const [uploads, setUploads] = useState({});
  const [errors, setErrors] = useState({});
  const [galleryFor, setGalleryFor] = useState(null);
  const latest = useRef(products);
  latest.current = products;
  const atLimit = products.length >= MAX_PRODUCTS;
  const overLimit = products.length > MAX_PRODUCTS;

  const uploadingCount = Object.values(uploads).filter(Boolean).length;
  useEffect(() => { onUploadingChange?.(uploadingCount > 0); }, [uploadingCount, onUploadingChange]);

  // Async results apply to the latest list, so edits made while an upload runs are kept.
  const updateProduct = (key, patch) => onChange(latest.current.map((product) => product._key === key ? { ...product, ...(typeof patch === "function" ? patch(product) : patch) } : product));
  const setError = (key, message) => setErrors((current) => ({ ...current, [key]: message }));

  // Puts an image in a slot: a number replaces that image, "new" adds one. The same image is never used twice.
  const placeImage = (key, slot, url) => updateProduct(key, (product) => {
    if (slot === "new") return { images: product.images.includes(url) ? product.images : [...product.images, url].slice(0, MAX_PRODUCT_IMAGES) };
    return { images: product.images.map((image, index) => index === slot ? url : image).filter((image, index) => image !== url || index === slot) };
  });

  const upload = async (key, slot, file) => {
    const fileError = checkImageFile(file);
    if (fileError) { setError(key, fileError); return; }
    setError(key, "");
    const uploadKey = `${key}:${slot}`;
    setUploads((current) => ({ ...current, [uploadKey]: true }));
    try {
      const media = await uploadProductImage(file);
      if (latest.current.some((product) => product._key === key)) placeImage(key, slot, media.url);
    } catch (uploadError) {
      setError(key, uploadError.message);
    } finally {
      setUploads((current) => ({ ...current, [uploadKey]: false }));
    }
  };

  const remove = (key) => {
    onChange(latest.current.filter((product) => product._key !== key));
    setErrors((current) => ({ ...current, [key]: "" }));
  };
  const isUploading = (key) => Object.entries(uploads).some(([uploadKey, active]) => active && uploadKey.startsWith(`${key}:`));

  const textField = (product, index, name, label, { required = false, type = "text", placeholder = "" } = {}) => <div className="pd-field" key={name}>
    <label className="pd-label" htmlFor={`offering-${index}-${name}`}>{label}{required ? <span className="pd-required">Required</span> : <span className="pd-optional">Optional</span>}</label>
    <input
      id={`offering-${index}-${name}`}
      className={inputClassName}
      type={type}
      min={type === "number" ? "0" : undefined}
      max={type === "number" ? "999999999" : undefined}
      step={type === "number" ? "any" : undefined}
      inputMode={type === "number" ? "decimal" : undefined}
      maxLength={limits[name]}
      placeholder={placeholder}
      aria-required={required || undefined}
      value={product[name] ?? ""}
      onChange={(event) => updateProduct(product._key, { [name]: event.target.value })}
    />
  </div>;

  const descriptionField = (product, index, placeholder) => <div className="pd-field pd-span">
    <label className="pd-label" htmlFor={`offering-${index}-description`}>Description<span className="pd-optional">Optional</span></label>
    <textarea id={`offering-${index}-description`} className={inputClassName} rows="2" maxLength={1200} placeholder={placeholder} value={product.description} onChange={(event) => updateProduct(product._key, { description: event.target.value })} />
    <span className="pd-counter">{product.description.length}/1200</span>
  </div>;

  return <section className={`pd is-${variant}`} aria-labelledby="pd-title">
    <div className="pd-heading">
      <div>
        <h3 id="pd-title">{title}</h3>
        <p>{description || `Add up to ${MAX_PRODUCTS} products or services. Product photos are used as references so AI images and videos show your real products.`}</p>
      </div>
      <div className="pd-heading-actions">
        <span className={`pd-count${atLimit ? " is-full" : ""}`}>{products.length}/{MAX_PRODUCTS} added</span>
        <Can permission={editPermission}><button type="button" className="pd-button is-secondary" disabled={atLimit} aria-describedby={atLimit ? "pd-limit" : undefined} onClick={() => onChange([...latest.current, emptyProduct()])}><i className="ti ti-plus" aria-hidden="true" />Add product or service</button></Can>
      </div>
    </div>
    {atLimit && <p id="pd-limit" className={`pd-limit${overLimit ? " is-warning" : ""}`} role="status">
      <i className={`ti ${overLimit ? "ti-alert-triangle" : "ti-info-circle"}`} aria-hidden="true" />
      {overLimit
        ? `You have ${products.length} items, more than the ${MAX_PRODUCTS} allowed. They are kept, but remove ${products.length - MAX_PRODUCTS} before adding another.`
        : `${limitMessage} Remove one to add another.`}
    </p>}

    {products.length === 0 && <div className="pd-empty"><span aria-hidden="true"><i className="ti ti-package" /></span><div><strong>No products or services added yet</strong><p>Products with photos help AI-generated visuals match what you actually sell.</p></div></div>}

    {products.map((product, index) => {
      const kind = product.type === "service" ? "Service" : product.type === "product" ? "Product" : "Item";
      const itemName = product.name.trim() || `${kind.toLowerCase()} ${index + 1}`;
      return <fieldset className={`pd-card${product.type ? ` is-${product.type}` : ""}`} key={product._key}>
        <legend className="pd-card-title">{product.name.trim() || `${kind} ${index + 1}`}</legend>
        <Can permission={editPermission}><button type="button" className="pd-card-remove" aria-label={`Remove item ${index + 1}`} disabled={isUploading(product._key)} onClick={() => remove(product._key)}><i className="ti ti-x" aria-hidden="true" />Remove</button></Can>

        <div className="pd-type" role="radiogroup" aria-labelledby={`pd-type-${product._key}`} aria-required="true">
          <span className="pd-label" id={`pd-type-${product._key}`}>Is this a product or a service?<span className="pd-required">Required</span></span>
          <div className="pd-type-options">
            {typeOptions.map(([value, label, icon, hint]) => <label key={value} className={`pd-type-option${product.type === value ? " is-selected" : ""}`}>
              <input type="radio" name={`pd-type-${product._key}`} value={value} checked={product.type === value} disabled={isUploading(product._key)} onChange={() => updateProduct(product._key, { type: value })} />
              <i className={`ti ${icon}`} aria-hidden="true" />
              <span><strong>{label}</strong><small>{hint}</small></span>
            </label>)}
          </div>
          {!product.type && <p className="pd-type-hint">Choose one to see the details for it.</p>}
        </div>

        {product.type === "product" && <div className="pd-fields">
          {textField(product, index, "name", "Product name", { required: true, placeholder: "e.g. Cold brew coffee" })}
          {textField(product, index, "category", "Category", { placeholder: "e.g. Beverages" })}
          {textField(product, index, "price", "Price", { type: "number", placeholder: "0.00" })}
          {descriptionField(product, index, "What it is, who it's for, what's in it")}
          <div className="pd-field pd-span">
            <span className="pd-label">Product images<span className="pd-optional">Optional · up to {MAX_PRODUCT_IMAGES}</span></span>
            <p className="pd-help">Clear photos from different angles. The first image is the main reference for AI visuals.</p>
            <div className="pd-images">
              {product.images.map((url, slot) => <ProductImageSlot key={url} imageUrl={url} isMain={slot === 0}
                label={`image ${slot + 1} of ${itemName}`}
                uploading={Boolean(uploads[`${product._key}:${slot}`])}
                onUpload={(file) => upload(product._key, slot, file)}
                onOpenGallery={() => setGalleryFor({ key: product._key, slot })}
                onRemove={() => updateProduct(product._key, (current) => ({ images: current.images.filter((_, imageIndex) => imageIndex !== slot) }))} />)}
              {product.images.length < MAX_PRODUCT_IMAGES && <ProductImageSlot
                label={`an image for ${itemName}`}
                uploading={Boolean(uploads[`${product._key}:new`])}
                onUpload={(file) => upload(product._key, "new", file)}
                onOpenGallery={() => setGalleryFor({ key: product._key, slot: "new" })} />}
            </div>
            {errors[product._key] && <p className="pd-error" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" />{errors[product._key]}</p>}
          </div>
          <ListInput id={`offering-${index}-features`} label="Key features" entries={product.features} placeholder="e.g. Organic Arabica beans" inputClassName={inputClassName} onChange={(features) => updateProduct(product._key, { features })} />
        </div>}

        {product.type === "service" && <div className="pd-fields">
          {textField(product, index, "name", "Service name", { required: true, placeholder: "e.g. Home barista workshop" })}
          {textField(product, index, "pricing", "Pricing", { placeholder: "e.g. ₹1,500 per session" })}
          {textField(product, index, "duration", "Duration", { placeholder: "e.g. 90 minutes" })}
          {textField(product, index, "category", "Category", { placeholder: "e.g. Training" })}
          {descriptionField(product, index, "What's included, who it's for, how it works")}
          <ListInput id={`offering-${index}-benefits`} label="Benefits" entries={product.benefits} placeholder="e.g. Learn latte art at home" inputClassName={inputClassName} onChange={(benefits) => updateProduct(product._key, { benefits })} />
        </div>}
      </fieldset>;
    })}

    {galleryFor && <ProductImagePicker
      currentUrl={galleryFor.slot === "new" ? "" : products.find((product) => product._key === galleryFor.key)?.images[galleryFor.slot]}
      onClose={() => setGalleryFor(null)}
      onSelect={(media) => {
        placeImage(galleryFor.key, galleryFor.slot, media.url);
        setError(galleryFor.key, "");
        setGalleryFor(null);
      }}
    />}
  </section>;
};

export default ProductDetailsEditor;
